import { DB_ONLY_CLICK_ID_KEYS } from './mixpanelTypes'
import type { MixpanelProperties } from './mixpanelTypes'
import { isValidNordyUserId } from './nordyUserId'
import type { CreditExhaustedMixpanelPayload } from './creditExhaustedTracking'

export interface MixpanelSdkAdapter {
  register: (properties: MixpanelProperties) => void
  register_once: (properties: MixpanelProperties) => void
  identify: (distinctId: string) => void
  people: {
    set: (properties: MixpanelProperties) => void
  }
  track: (eventName: string, properties?: MixpanelProperties) => void
  reset: () => void
}

interface MixpanelRuntimeOptions {
  getRouteContext?: () => MixpanelProperties
}

const BLOCKED_MIXPANEL_KEYS = new Set<string>([
  ...DB_ONLY_CLICK_ID_KEYS,
  '$mp_autocapture'
])

function sanitizeMixpanelProperties(
  properties: MixpanelProperties = {}
): MixpanelProperties {
  return Object.fromEntries(
    Object.entries(properties).filter(
      ([key, value]) => !BLOCKED_MIXPANEL_KEYS.has(key) && value !== undefined
    )
  )
}

function failOpen(operation: () => unknown): void {
  try {
    operation()
  } catch {
    return
  }
}

function getBrowserRouteContext(): MixpanelProperties {
  if (typeof window === 'undefined') return { page_slug: '/', lang: 'en' }

  return {
    page_slug: window.location.pathname + window.location.search || '/',
    lang: 'en'
  }
}

export function createMixpanelRuntime(
  adapter: MixpanelSdkAdapter,
  { getRouteContext = getBrowserRouteContext }: MixpanelRuntimeOptions = {}
) {
  function track(eventName: string, properties: MixpanelProperties = {}): void {
    failOpen(() => {
      adapter.track(
        eventName,
        sanitizeMixpanelProperties({
          ...properties,
          ...getRouteContext()
        })
      )
    })
  }

  return {
    register(properties: MixpanelProperties): void {
      failOpen(() => adapter.register(sanitizeMixpanelProperties(properties)))
    },
    registerOnce(properties: MixpanelProperties): void {
      failOpen(() =>
        adapter.register_once(sanitizeMixpanelProperties(properties))
      )
    },
    identify(distinctId: unknown): void {
      if (!isValidNordyUserId(distinctId)) return
      failOpen(() => adapter.identify(distinctId))
    },
    setPeople(properties: MixpanelProperties): void {
      failOpen(() => adapter.people.set(sanitizeMixpanelProperties(properties)))
    },
    reset(): void {
      failOpen(() => adapter.reset())
    },
    events: {
      firstVisit: (properties: MixpanelProperties): void =>
        track('first_visit', properties),
      userEngagement: (properties: MixpanelProperties): void =>
        track('user_engagement', properties),
      creditExhausted: (properties: CreditExhaustedMixpanelPayload): void =>
        track('credit_exhausted', properties),
      logoutSuccess: (): void => track('logout_success')
    }
  }
}

type MixpanelWindow = Window & {
  mixpanel?: Partial<MixpanelSdkAdapter> & {
    people?: Partial<MixpanelSdkAdapter['people']>
  }
}

function getMixpanel() {
  if (typeof window === 'undefined') return undefined
  return (window as MixpanelWindow).mixpanel
}

const browserAdapter: MixpanelSdkAdapter = {
  register: (properties) => getMixpanel()?.register?.(properties),
  register_once: (properties) => getMixpanel()?.register_once?.(properties),
  identify: (distinctId) => getMixpanel()?.identify?.(distinctId),
  people: {
    set: (properties) => getMixpanel()?.people?.set?.(properties)
  },
  track: (eventName, properties) =>
    getMixpanel()?.track?.(eventName, properties),
  reset: () => getMixpanel()?.reset?.()
}

export const mixpanelRuntime = createMixpanelRuntime(browserAdapter)
